import React, { useMemo } from "react";

import { productAnalytics, useTranslation } from "utils";

import { CURRENCY_CODE, CurrencyCode } from "consts";

import { Button, Icon, Option, Select, SoilSightLoader, Typography } from "components";

import { BetaCaveat } from "pages/app/scenario-analysis/components/BetaNotice";
import { KeyInsightCards } from "pages/app/scenario-analysis/components/KeyInsightCards";
import { SectionHeading } from "pages/app/scenario-analysis/components/SectionHeading";
import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { SoilSightOutcomesByScenario, ViewMode, scenarioLineColor } from "pages/app/scenario-analysis/utils/forecast";
import { hasCompletedProjection } from "pages/app/scenario-analysis/utils/keyInsightsHelpers";
import { tourTargetProps } from "pages/app/scenario-analysis/utils/outcomesTourTarget";
import { scenariosHaveIdenticalCropYears } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { buildScenarioComparison, canCompareWithBaseline, resolveComparisonSelection } from "pages/app/scenario-analysis/utils/scenarioComparison";
import { ScenarioResilienceByScenarioId } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";
import { referenceIsReadyToCalculate } from "pages/app/scenario-analysis/utils/scenarioValidation";

export interface ScenarioComparisonBoxProps {
  scenarios: ScenarioData[];
  soilSightOutcomes: SoilSightOutcomesByScenario | null;
  projectionYears: number;
  currency: CurrencyCode | null;
  /** Resilience scores keyed by scenario id, for the resilience card's figure and band. */
  resilienceScores: ScenarioResilienceByScenarioId;
  /** Figures no longer match the inputs — the box hides them rather than showing stale numbers. */
  isStale: boolean;
  isCalculating: boolean;
  selectedScenarioId: string | null;
  onSelectScenario: (scenarioId: string) => void;
  onAddScenario: () => void;
  /** Sends the farmer to the builder to complete the baseline rotation. */
  onAddBaselineRotation: () => void;
  onRecalculate: () => void;
  /** Receives the scenario on show, so the insights modal opens on the same one. */
  onGetMoreKeyInsights: (scenarioId: string | null) => void;
  // TODO: drop this prop and always render the "Get more key insights" footer once the feature is re-enabled.
  /** Hidden for now; pass true to bring back the "Get more key insights" footer. */
  isKeyInsightsActionEnabled?: boolean;
  /** A card was clicked: switches the chart below to that view and scrolls it into position. */
  onOpenChartView: (view: ViewMode, scenarioId?: string) => void;
  activeChartView?: ViewMode;
  onOpenBetaInfo: () => void;
}

export const ScenarioComparisonBox = ({
  scenarios,
  soilSightOutcomes,
  projectionYears,
  currency,
  resilienceScores,
  isStale,
  isCalculating,
  selectedScenarioId,
  onSelectScenario,
  onAddScenario,
  onAddBaselineRotation,
  onRecalculate,
  onGetMoreKeyInsights,
  isKeyInsightsActionEnabled = false,
  onOpenChartView,
  activeChartView,
  onOpenBetaInfo,
}: ScenarioComparisonBoxProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const resolvedCurrency = currency ?? CURRENCY_CODE.EUR;

  const { baseline, comparisonScenarios, selected } = useMemo(() => resolveComparisonSelection(scenarios, selectedScenarioId), [scenarios, selectedScenarioId]);

  const baselineHasResults = Boolean(baseline && hasCompletedProjection(baseline.id, soilSightOutcomes));
  // Both sides need rows: with results for the baseline alone the comparison reads as a confident -100%.
  const comparisonHasResults = Boolean(selected && hasCompletedProjection(selected.id, soilSightOutcomes));
  const baselineIsReadyToCalculate = referenceIsReadyToCalculate(scenarios);
  const comparisonMatchesBaseline = useMemo(() => scenariosHaveIdenticalCropYears(baseline, selected), [baseline, selected]);

  const comparison = useMemo(() => {
    if (!baseline || !selected) return null;
    return buildScenarioComparison({
      outcomes: soilSightOutcomes,
      baselineScenarioId: baseline.id,
      comparisonScenarioId: selected.id,
      projectionYears,
    });
  }, [baseline, selected, soilSightOutcomes, projectionYears]);

  const bodyVariant: "no-baseline" | "no-comparison" | "identical" | "pending" | "results" = (() => {
    // Missing results with a complete rotation is a recalculation, not a setup step.
    if (!baselineIsReadyToCalculate && !isCalculating) return "no-baseline";
    if (comparisonScenarios.length === 0) return "no-comparison";
    if (isCalculating) return "pending";
    // Ahead of the figures: identical inputs make every one of them zero, which reads as a real result.
    if (comparisonMatchesBaseline) return "identical";
    if (isStale || !canCompareWithBaseline(baseline, selected, soilSightOutcomes)) return "pending";
    return "results";
  })();

  // The picker travels with the header, so the identical state keeps it — the copy tells them to use it.
  const showHeader = bodyVariant === "pending" || bodyVariant === "results" || bodyVariant === "identical";
  const showKeyInsightsAction = bodyVariant === "pending" || bodyVariant === "results";

  const scenarioPicker = selected ? (
    <span className="scenario-comparison-scenario-picker">
      <span className="scenario-comparison-scenario-dot" style={{ background: scenarioLineColor(scenarios, selected.id) }} aria-hidden />
      <Select
        value={selected.id}
        onChange={(scenarioId: string) => {
          productAnalytics.track("soilsights:scenario_compare", { scenario_count: scenarios.length });
          onSelectScenario(scenarioId);
        }}
        popupMatchSelectWidth={false}
        popupClassName="scenario-comparison-dropdown"
        className="scenario-comparison-scenario-select"
        dataTestId="scenario-comparison-scenario-select"
        virtual={false}
      >
        {comparisonScenarios.map((scenario) => (
          <Option key={scenario.id} value={scenario.id} title={scenario.title}>
            {scenario.title}
          </Option>
        ))}
      </Select>
    </span>
  ) : (
    <span />
  );

  const renderHeader = () => (
    <SectionHeading
      title={t("scenarioComparison.title")}
      supporting={t("scenarioComparison.subtitle")}
      dataTestId="scenario-comparison-heading"
      /* One picker, for the change being compared — the baseline stays fixed. */
      control={
        <>
          <Typography variant="paragraph-s" className="scenario-comparison-comparing-label">
            {t("scenarioComparison.comparingLabel")}
          </Typography>
          {scenarioPicker}
        </>
      }
    />
  );

  const EMPTY_STATE_COPY = {
    "no-baseline": { titleKey: "noBaselineTitle", bodyKey: "noBaselineBody", ctaKey: "addBaselineRotation" },
    "no-comparison": { titleKey: "noComparisonTitle", bodyKey: "noComparisonBody", ctaKey: "addScenario" },
    identical: { titleKey: "identicalTitle", bodyKey: "identicalBody", ctaKey: "addScenario" },
  } as const;

  const renderEmptyState = (variant: keyof typeof EMPTY_STATE_COPY) => {
    const { titleKey, bodyKey, ctaKey } = EMPTY_STATE_COPY[variant];
    return (
      <div className="scenario-comparison-empty" data-testid={`scenario-comparison-empty-${variant}`}>
        <span className="scenario-comparison-empty-icon" aria-hidden>
          <Icon iconName="klim/crop-footprint" size={48} height={40} />
        </span>
        <Typography variant="paragraph-m" bold className="scenario-comparison-empty-title">
          {t(`scenarioComparison.${titleKey}`)}
        </Typography>
        <Typography variant="paragraph-m" color="grey-600" className="scenario-comparison-empty-body">
          {t(`scenarioComparison.${bodyKey}`)}
        </Typography>
        <Button
          type="primary"
          size="middle"
          onClick={variant === "no-baseline" ? onAddBaselineRotation : onAddScenario}
          className="scenario-comparison-empty-cta"
          dataTestId={`scenario-comparison-empty-cta-${variant}`}
        >
          {t(`scenarioComparison.${ctaKey}`)}
        </Button>
      </div>
    );
  };

  const renderStaleState = () => (
    <div className="scenario-comparison-stale" data-testid="scenario-comparison-stale">
      {isCalculating ? (
        <SoilSightLoader size="m" dataTestId="scenario-comparison-calculating" />
      ) : (
        <>
          <span className="scenario-comparison-stale-icon" aria-hidden>
            <Icon iconName="tabler/loader" size="l" />
          </span>
          <Typography variant="paragraph-m" bold className="scenario-comparison-stale-title">
            {t("scenarioComparison.recalculationNeededTitle")}
          </Typography>
          <Typography variant="paragraph-m" className="scenario-comparison-stale-body">
            {t("scenarioComparison.recalculationNeededBody")}
          </Typography>
          <Button
            type="primary"
            size="middle"
            onClick={onRecalculate}
            icon={<Icon iconName="tabler/refresh" size="s" color="white" />}
            iconLeft
            className="scenario-comparison-stale-cta"
            dataTestId="scenario-comparison-stale-cta"
          >
            {t("scenarioComparison.updateForecast")}
          </Button>
        </>
      )}
    </div>
  );

  const renderResults = () => {
    if (!comparison || !baseline || !selected) return null;

    return (
      <div className="scenario-comparison-results" data-testid="scenario-comparison-results">
        <KeyInsightCards
          comparison={comparison}
          baseline={baseline}
          subject={selected}
          soilSightOutcomes={soilSightOutcomes}
          projectionYears={projectionYears}
          currency={resolvedCurrency}
          resilienceScores={resilienceScores}
          onOpenChartView={onOpenChartView}
          activeView={activeChartView}
        />
        <BetaCaveat onOpenBetaInfo={onOpenBetaInfo} />
      </div>
    );
  };

  const renderBody = () => {
    if (bodyVariant === "no-baseline") return renderEmptyState("no-baseline");
    if (bodyVariant === "no-comparison") return renderEmptyState("no-comparison");
    if (bodyVariant === "identical") return renderEmptyState("identical");
    if (bodyVariant === "pending") return renderStaleState();
    return renderResults();
  };

  return (
    <section className="scenario-comparison" data-testid="scenario-comparison" {...tourTargetProps("comparison")}>
      <div className="scenario-comparison-content">
        {showHeader ? renderHeader() : null}
        {renderBody()}
      </div>
      {/* The driver breakdown ("What's making the difference?") was removed, not relocated: its shares
          could read "+138% of other factors". Leave it out until the driver calculation is fixed. */}
      {/* The band is the card's bottom edge either way; the action only appears once there is a comparison. */}
      {isKeyInsightsActionEnabled &&
        (showKeyInsightsAction ? (
          <button
            type="button"
            className="scenario-comparison-footer"
            onClick={() => onGetMoreKeyInsights(selected?.id ?? null)}
            disabled={!baselineHasResults || !comparisonHasResults || isStale || isCalculating}
            data-testid="scenario-comparison-more-insights"
          >
            {t("scenarioComparison.getMoreKeyInsights")}
            <Icon iconName="tabler/arrow-right" size="s" className="scenario-comparison-footer-icon" />
          </button>
        ) : (
          <div className="scenario-comparison-footer scenario-comparison-footer--empty" aria-hidden />
        ))}
    </section>
  );
};

export default ScenarioComparisonBox;
